import React, { use } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SelectField from 'shared/components/formFields/select-field';

import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import DomipProviderContext from 'features/systems/domip/providers/contexts/domip-provider-context';
import styles from './domip-configuration-component.module.scss';

export const RestartTimerConstants = {
  off: 0,
  on: 900,
};

const DomipConfigurationComponent = (): React.JSX.Element => {
  const { t } = useTranslation('domip');

  const { control } = useFormContext();

  const { readDomipConfigurationData } = use(DomipProviderContext);

  const {
    systemsDetailsHook: { viewingMode },
  } = useSystemsContextProvider();

  const restartTimerOptions = [
    <MenuItem key={`system-restart-timer-off`} value={RestartTimerConstants.off}>
      {t('configuration.inputForm.timer.off')}
    </MenuItem>,
    <MenuItem key={`system-restart-timer-on`} value={RestartTimerConstants.on}>
      {t('configuration.inputForm.timer.on')}
    </MenuItem>,
  ];

  const getDhcpTimerTranslation = (value: number | undefined): string => {
    return value && value > 0 ? 'configuration.dhcpRestartTimer' : 'configuration.dhcpRestartTimerOff';
  };

  const renderViewingConfigurationSettings = (): React.JSX.Element => {
    return (
      <div className={styles.configurationSettings}>
        <Typography variant="h5">{t('configuration.configuration')}</Typography>
        <Typography variant="subtitle1">
          {t('configuration.hostName')} {readDomipConfigurationData?.hostName}
        </Typography>

        <Typography variant="subtitle1">
          {t(getDhcpTimerTranslation(readDomipConfigurationData?.dhcpRestartTimeAfterLostContact))}
        </Typography>
      </div>
    );
  };

  const renderEditingConfigurationSettings = (): React.JSX.Element => {
    return (
      <div className={styles.configurationSettings}>
        <Typography variant="h5">{t('configuration.configuration')}</Typography>
        <Controller
          name={'hostName'}
          control={control}
          rules={{
            required: t('configuration.inputForm.hostNameRequired'),
            validate: (value) => {
              if (value && /\s/.test(value)) {
                return t('configuration.inputForm.hostNameNoWhitespaces');
              }
              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={t('configuration.inputForm.hostNameLabel')}
              variant="outlined"
              slotProps={{ htmlInput: { maxLength: 15 } }}
              error={!!error}
              helperText={error?.message}
              onChange={(e) => {
                const filteredValue = e.target.value.replaceAll(/\W/g, '');
                field.onChange(filteredValue);
              }}
            />
          )}
        />

        <Controller
          name="dhcpRestartTimeAfterLostContact"
          control={control}
          render={({ field }) => (
            <SelectField {...field} label={t('configuration.inputForm.dhcpRestartTimeAfterLostContactInputLabel')} variant="outlined">
              {restartTimerOptions}
            </SelectField>
          )}
        />
      </div>
    );
  };

  return (
    <>
      <div className={styles.configuration}>
        {viewingMode === 'viewing' && <>{renderViewingConfigurationSettings()}</>}

        {viewingMode === 'editing' && <>{renderEditingConfigurationSettings()}</>}
      </div>
      <Divider sx={{ width: '100%' }} />
    </>
  );
};

export default DomipConfigurationComponent;
